@if (!isLoading) {
<main class="content-container" role="main">
  @if (showRedesignMessage) {
  <section aria-label="Feature redesign notice">
    <dxo-card-responsive [bodyHeader]="claimsBodyHeader">
      <div id="body-content">{{ cardBody }}</div>
      <dxo-button-v2 icon="open_in_new" variant="primary" [label]="detailsButtonLabel" [href]="detailsURL" />
    </dxo-card-responsive>
  </section>
  }
  @if (!showRedesignMessage) {
  <section class="full-width" aria-label="Claims search">
    <dxo-card-responsive class="claims-card">
      <header class="results-header">
        <h1 class="heading-md bold">Search claims</h1>
        <dxo-button-v2 variant="secondary" label="Create new claim" (handleClick)="createNewClaim()" size="small" />
      </header>
      <section class="parent" aria-label="Search interface">
        <div class="row no-gap">
          <div class="row triple-even jagged-end-expand">
            <div class="Triple-even growing columns">
              <dxo-text-input [label]="searchInputLabel" class="search-input" [placeholder]="searchInputLabel"
                maxlength="80" [value]="searchText" [fullWidth]="true" (valueChange)="onSearchInput($event)"
                (keyup.enter)="onSearch()" role="searchbox" aria-label="Search claims by name or claim number"
                [attr.aria-describedby]="resultsCountMessage() ? 'search-results-count' : null"
                [dxoDataset]="[{ selector: 'input', data: { e2e: 'claim-search-field' } }]" />
              @if (resultsCountMessage()) {
              <p class="results-count" id="search-results-count" aria-live="polite" data-e2e="search-results-text">
                <span [innerHTML]="resultsCountMessage()"></span>
              </p>
              }
            </div>
            <div class="drawer-filter-container claims-filter-container">
              <dxo-drawer-filter [autoClose]="true" buttonText="Filter results" buttonIcon="filter_alt"
                [filters]="filters" [topAnchor]="87" (clearResults)="onClearFilters()" [dxoDataset]="[
                  { selector: '[class=drawer-filter-container] > dxo-button-v2 > button', data: { e2e: 'filter-result-button' } },
                  { selector: '[class=clear-filters] > dxo-button-v2', data: { e2e: 'clear-filters-link' } }
                ]" />
            </div>
          </div>
        </div>
      </section>
      <nav class="tabs-toggle" aria-label="Claims filter navigation">
        @if (!combineTabs) {
        <dxo-tabs #tabsRef [default]="selectedTab()" (tabChanged)="onTabChange($event)" role="tablist"
          aria-label="Filter claims by status">
          <dxo-tab [header]="ClaimTabNameEnum.Active" [badges]="tabBadges()[ClaimTabNameEnum.Active]" />
          <dxo-tab [header]="ClaimTabNameEnum.Closed" [badges]="tabBadges()[ClaimTabNameEnum.Closed]" />
        </dxo-tabs>
        }
        @if (combineTabs) {
        <dxo-tabs [default]="ClaimTabNameEnum.All" role="tablist" aria-label="View all claims">
          <dxo-tab [header]="ClaimTabNameEnum.All" [badges]="tabBadges()[ClaimTabNameEnum.All]" />
        </dxo-tabs>
        }
        <div class="toggle-container">
          <dxo-toggle text="Combine tabs" [(value)]="combineTabs"  data-e2e="combine-toggle"/>
        </div>
      </nav>
      <section aria-label="Search results">
        @if (shouldShowEmptyState()) {
        <dxo-alert class="empty-state-alert" status="info" [showBorder]="false" [dismissable]="false">
          <span [innerHTML]="emptyClaimsMessage()"></span>
        </dxo-alert>
        }
        @if (shouldShowCrossTabSuggestion()) {
        <p>
          <span [innerHTML]="noClaimsResultsMessage().message"></span>
          @if (noClaimsResultsMessage().tabToShow) {
          <a class="text-link" href="javascript:void(0)" (click)="activateTab(noClaimsResultsMessage().tabToShow!)" 
             [attr.aria-label]="'Switch to ' + noClaimsResultsMessage().tabToShow + ' tab to see results'">
            See results in {{ noClaimsResultsMessage().tabToShow }}
          </a>
          }
        </p>
        }
        @if (filteredClaims().length > 0) {
        <div class="claims-table-container">
          <dxo-data-table [showCardViewWhenMobile]="true" [tableHeaderWrap]="false" [headers]="tableHeaders"
            [items]="filteredClaims()" [showDividers]="true" [showHover]="false" aria-label="Claims search results" data-e2e="claims-search-table">
            <ng-template #sortIcon let-field>
              <dxo-icon [class.ascending]="sortIconAscending()[field]"
                [color]="searchParameter.sortField === field | getSortIconColor" [fontSize]="20" [right]="true"
                aria-hidden="true">
                sort
              </dxo-icon>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.ClaimId">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.ClaimId]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.ClaimId]"
                data-e2e="claim-id-sort"
                (click)="onClickTableHeader(ClaimField.ClaimId)"
                (keydown)="onKeydownTableHeader($event, ClaimField.ClaimId)">
                {{ headerTexts()[ClaimField.ClaimId] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.ClaimId }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.Name">
              <span tabindex="0" class="sort-header" role="button" 
                [attr.aria-label]="headerAriaLabels()[ClaimField.Name]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.Name]" 
                (click)="onClickTableHeader(ClaimField.Name)"
                (keydown)="onKeydownTableHeader($event, ClaimField.Name)"
                data-e2e="claim-name-sort">
                {{ headerTexts()[ClaimField.Name] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.Name }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.ClaimType">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.ClaimType]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.ClaimType]"
                (click)="onClickTableHeader(ClaimField.ClaimType)"
                (keydown)="onKeydownTableHeader($event, ClaimField.ClaimType)"
                data-e2e="claim-type-sort">
                {{ headerTexts()[ClaimField.ClaimType] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.ClaimType }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.Status">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.Status]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.Status]"
                (click)="onClickTableHeader(ClaimField.Status)"
                (keydown)="onKeydownTableHeader($event, ClaimField.Status)"
                data-e2e="claim-status-sort">
                {{ headerTexts()[ClaimField.Status] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.Status }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.Received">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.Received]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.Received]"
                (click)="onClickTableHeader(ClaimField.Received)"
                (keydown)="onKeydownTableHeader($event, ClaimField.Received)"
                data-e2e="claim-received-sort">
                {{ headerTexts()[ClaimField.Received] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.Received }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.Started">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.Started]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.Started]"
                [attr.aria-describedby]="'start-column-tooltip'" (click)="onClickTableHeader(ClaimField.Started)"
                (keydown)="onKeydownTableHeader($event, ClaimField.Started)"
                data-e2e="claim-started-sort">
                {{ headerTexts()[ClaimField.Started] }}
                <dxo-tooltip id="start-column-tooltip" [heading]="tooltipHeadings()[ClaimField.Started]"
                  [body]="tooltipBodies()[ClaimField.Started]">
                  <dxo-icon [right]="false" [left]="true" class="icon-left" aria-hidden="true">
                    info_outline
                  </dxo-icon>
                </dxo-tooltip>
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.Started }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.Division">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.Division]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.Division]"
                (click)="onClickTableHeader(ClaimField.Division)"
                (keydown)="onKeydownTableHeader($event, ClaimField.Division)"
                data-e2e="claim-division-sort">
                {{ headerTexts()[ClaimField.Division] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.Division }" />
              </span>
            </ng-template>
            <ng-template [dxoHeaderKey]="ClaimField.LeaveType">
              <span tabindex="0" class="sort-header" role="button"
                [attr.aria-label]="headerAriaLabels()[ClaimField.LeaveType]"
                [attr.aria-sort]="headerAriaSortValues()[ClaimField.LeaveType]"
                (click)="onClickTableHeader(ClaimField.LeaveType)"
                (keydown)="onKeydownTableHeader($event, ClaimField.LeaveType)"
                data-e2e="claim-leave-sort">
                {{ headerTexts()[ClaimField.LeaveType] }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: ClaimField.LeaveType }" />
              </span>
            </ng-template>
            <ng-template [dxoKey]="ClaimField.ClaimId" let-item let-index="index">
              <a href="javascript:void(0)" class="semibold"
                [attr.aria-label]="'View details for claim ' + item.claimId + ' for ' + item.name"
                (click)="viewClaimDetail(item.claimId)"
                [attr.data-e2e]="'claim-id-' + index">
                {{ item.claimId }}
              </a>
            </ng-template>
            <ng-template [dxoKey]="ClaimField.ClaimType" let-item>
              {{ item.claimType }}
            </ng-template>
            <ng-template [dxoKey]="ClaimField.Status" let-item>
              <dxo-status-indicator [color]="item.statusColor" [text]="item.status" />
            </ng-template>
            <ng-template [dxoKey]="ClaimField.Received" let-item>
              {{ item.received }}
            </ng-template>
            <ng-template [dxoKey]="ClaimField.Started" let-item>
              {{ item.started }}
            </ng-template>
            <ng-template [dxoKey]="ClaimField.Division" let-item>
              {{ item.division }}
            </ng-template>
            <ng-template [dxoKey]="ClaimField.LeaveType" let-item>
              {{ item.leaveType }}
            </ng-template>
          </dxo-data-table>
          <footer class="paginator">
            <dxo-server-pagination [itemCount]="totalClaims" [currentPage]="searchParameter.page"
              [pageSize]="searchParameter.pageSize" (pageChange)="onPageChange($event)"
              (pageSizeChange)="onPageSizeChange($event)" />
          </footer>
        </div>
        }
      </section>
    </dxo-card-responsive>
  </section>
  }
</main>
}
@if (isLoading) {
<section class="loader-container" aria-label="Loading claims data">
  <dxo-circular-loader size="medium" color="blue" />
</section>
}